import { Invoice, InvoiceStatus } from 'APIs/invoices/invoices.types';
import Spinner from 'Components/Spinner/Spinner';
import { DateTime } from 'luxon';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { FaHourglassHalf } from 'react-icons/fa';
import { IoMdDownload } from 'react-icons/io';
import { RiMailSendLine } from 'react-icons/ri';
import { TbCashRegister } from 'react-icons/tb';
import { Currency, toCurrency } from 'utils/currency';

import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { ActionButton, ActionWaiting, InvoiceDataTableContainer, MessageWrapper } from './styles';

// createTheme creates a new theme named solarized that overrides the build in dark theme
createTheme('polarized', TableThemeSettings);

interface Props {
  invoiceList?: Array<Invoice>;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  limit: number;
  activePage: number;
  isErrorRequest?: boolean;
  handleAdministerInvoice: (invoiceId: string, action: 'download' | 'resend_email' | 'mark_as_manually_paid') => void;
  hideMerchantColumn?: boolean;
  actionInvoices?: object;
  actionStates?: Array<InvoiceStatus>;
}

function InvoiceDataTable({
  invoiceList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  activePage,
  isErrorRequest,
  handleAdministerInvoice,
  hideMerchantColumn = false,
  actionInvoices,
  actionStates,
}: Props) {
  const ManuallyPaidIcon = TbCashRegister as FunctionComponent;
  const SendEmailIcon = RiMailSendLine as FunctionComponent;
  const HourGlassIcon = FaHourglassHalf as FunctionComponent;
  const DownloadIcon = IoMdDownload as FunctionComponent;

  const columns: TableColumn<Invoice>[] = useMemo(() => {
    const first = [
      {
        name: 'Invoice ID',
        selector: (row: Invoice) => row.invoiceId,
        minWidth: '150px',
      },
      {
        name: 'Invoice reference',
        selector: (row: Invoice) => row.invoiceReference,
        minWidth: '150px',
      },
      {
        name: 'Amount',
        selector: (row: Invoice) => toCurrency(row.invoiceAmount, row.currency as Currency),
        width: '110px',
        right: true,
      },
      {
        name: '',
        selector: (row: Invoice) => row.currency as Currency,
        width: '40px',
        left: true,
      },
      {
        name: 'Issued on',
        selector: (row: Invoice) => `${DateTime.fromISO(row.createdAt).toFormat('dd/LL/yyyy, HH:mm')}`,
        minWidth: '80px',
      },
    ];

    const second = [
      {
        name: 'Merchant ID',
        selector: (row: Invoice) => {
          return <div>{`${row.merchantId}`}</div>;
        },
        width: '180px',
      },
      {
        name: 'Business name',
        selector: (row: Invoice) => {
          return <div title={`${row.merchantBusinessName}`}>{`${row.merchantBusinessName}`}</div>;
        },
        width: '200px',
      },
    ];

    const third = [
      {
        name: 'Status',
        cell: (row: Invoice) => {
          return <div>{`${row.status}`}</div>;
        },
        width: '100px',
      },
      {
        name: 'Summary',
        selector: (row: Invoice) => {
          return <div title={`${row.summary}`}>{`${row.summary}`}</div>;
        },
        width: '75px',
      },
      {
        name: '',
        cell: (row: Invoice) => {
          return actionStates && actionStates?.includes(row.status) ? (
            <div>
              {actionInvoices && row.invoiceId in actionInvoices ? (
                <ActionWaiting>
                  <HourGlassIcon />
                </ActionWaiting>
              ) : (
                <div className="hover-button-wrapper">
                  <ActionButton
                    className="hover-button"
                    title="Download"
                    onClick={() => handleAdministerInvoice(row.invoiceId, 'download')}
                  >
                    <DownloadIcon />
                  </ActionButton>
                  <ActionButton
                    className="hover-button"
                    title="Resend email"
                    onClick={() => handleAdministerInvoice(row.invoiceId, 'resend_email')}
                  >
                    <SendEmailIcon />
                  </ActionButton>
                  <ActionButton
                    className="hover-button"
                    title="Mark as manually paid"
                    onClick={() => handleAdministerInvoice(row.invoiceId, 'mark_as_manually_paid')}
                  >
                    <ManuallyPaidIcon />
                  </ActionButton>
                </div>
              )}
            </div>
          ) : (
            ''
          );
        },
        width: '100px',
        center: true,
      },
    ];

    return hideMerchantColumn ? [...first, ...third] : [...first, ...second, ...third];
  }, [
    DownloadIcon,
    HourGlassIcon,
    ManuallyPaidIcon,
    SendEmailIcon,
    actionInvoices,
    actionStates,
    handleAdministerInvoice,
    hideMerchantColumn,
  ]);

  return (
    <InvoiceDataTableContainer>
      <DataTable
        pagination
        responsive
        columns={columns}
        data={invoiceList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="25px" width="25px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationPerPage={limit}
        paginationDefaultPage={activePage}
        sortServer
        persistTableHead
        defaultSortFieldId={4}
        defaultSortAsc={false}
        paginationRowsPerPageOptions={[20, 40, 60, 80, 100]}
      />
    </InvoiceDataTableContainer>
  );
}

export default InvoiceDataTable;
